import { useEffect, useMemo, useRef, useState } from "react";
import type { RecycleItem, RecycleKind, User } from "@board/shared";
import { api } from "./api";
import { useBoard } from "./store";

const KIND_LABEL: Record<RecycleKind, string> = {
  client: "Client board",
  task: "Sticky",
  subtask: "Subtask",
  comment: "Comment",
  text: "Board text",
  attachment: "Attachment",
};

function timeLeft(purgeAt: string): string {
  const days = Math.max(0, Math.ceil((new Date(purgeAt).getTime() - Date.now()) / 86_400_000));
  if (days === 0) return "Removed today";
  return `${days} day${days === 1 ? "" : "s"} left`;
}

function deletedWhen(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  }).format(new Date(value));
}

export function RecycleBin({ me, onDone }: { me: User; onDone: () => void }) {
  const refresh = useBoard((state) => state.refresh);
  const close = useRef<HTMLButtonElement>(null);
  const [items, setItems] = useState<RecycleItem[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    api.recycleBin.list()
      .then((result) => { setItems(result.items); setState("ready"); })
      .catch(() => setState("error"));
  }, []);

  useEffect(() => {
    close.current?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDone();
    };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [onDone]);

  const groups = useMemo(() => {
    const map = new Map<string, { name: string; items: RecycleItem[] }>();
    for (const item of items) {
      const name = item.client_name ?? "Open board";
      // Native worklist tasks have no client id, but the database supplies the
      // worklist name. Keep each list in its own bin section instead of folding
      // every internal task into the generic open-board group.
      const key = item.client_id ?? (item.client_name ? `worklist:${item.client_name}` : "open-board");
      const group = map.get(key);
      if (group) group.items.push(item);
      else map.set(key, { name, items: [item] });
    }
    return [...map.entries()];
  }, [items]);

  async function restore(item: RecycleItem) {
    setBusy(`${item.kind}:${item.id}`);
    setProblem(null);
    try {
      await api.recycleBin.restore(item.kind, item.id);
      setItems((current) => current.filter((entry) => entry !== item));
      await refresh();
    } catch {
      setProblem("That item couldn’t be restored. Its client board or task may still be in the bin.");
    } finally {
      setBusy(null);
    }
  }

  async function purge(item: RecycleItem) {
    if (!window.confirm(`Delete “${item.label}” forever? This cannot be undone.`)) return;
    setBusy(`${item.kind}:${item.id}`);
    setProblem(null);
    try {
      await api.recycleBin.purge(item.kind, item.id);
      setItems((current) => current.filter((entry) => entry !== item));
      await refresh();
    } catch {
      setProblem("That item couldn’t be permanently deleted.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="trashbackdrop" role="presentation" onPointerDown={(event) => {
      if (event.target === event.currentTarget) onDone();
    }}>
      <section className="trashpanel" role="dialog" aria-modal="true" aria-labelledby="trash-title">
        <header className="trashpanel__head">
          <div>
            <p className="trashpanel__eyebrow">smrt board</p>
            <h2 id="trash-title">Recycle bin</h2>
            <p>Items stay here for 30 days before they are permanently removed.</p>
          </div>
          <button ref={close} className="trashpanel__close" onClick={onDone} aria-label="Close recycle bin">×</button>
        </header>

        {problem ? <p className="trashpanel__problem" role="alert">{problem}</p> : null}

        <div className="trashlist" aria-busy={state === "loading"}>
          {state === "loading" ? <p className="trashlist__empty">Opening the recycle bin…</p> : null}
          {state === "error" ? <p className="trashlist__empty" role="alert">The recycle bin couldn’t be loaded.</p> : null}
          {state === "ready" && items.length === 0 ? (
            <div className="trashlist__empty trashlist__empty--clean">
              <span aria-hidden="true">✓</span>
              <strong>The recycle bin is empty</strong>
              <p>Deleted boards, stickies and files will wait here for 30 days.</p>
            </div>
          ) : null}

          {groups.map(([clientId, group]) => (
            <section className="trashgroup" key={clientId}>
              <h3>{group.name}</h3>
              {group.items.map((item) => {
                const key = `${item.kind}:${item.id}`;
                return (
                  <article className="trashitem" key={key}>
                    <div className={`trashitem__icon trashitem__icon--${item.kind}`} aria-hidden="true" />
                    <div className="trashitem__body">
                      <span className="trashitem__kind">{KIND_LABEL[item.kind]}</span>
                      <strong>{item.label}</strong>
                      <span>
                        Deleted {deletedWhen(item.deleted_at)}
                        {item.deleted_by_name ? ` by ${item.deleted_by_name}` : ""}
                      </span>
                    </div>
                    <span className="trashitem__time">{timeLeft(item.purge_at)}</span>
                    <div className="trashitem__actions">
                      <button disabled={busy === key} onClick={() => void restore(item)}>
                        {busy === key ? "Working…" : "Restore"}
                      </button>
                      {me.role === "admin" ? (
                        <button className="trashitem__forever" disabled={busy === key}
                          onClick={() => void purge(item)}>Delete forever</button>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </section>
          ))}
        </div>
      </section>
    </div>
  );
}
